import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { WithdrawalFormData, WithdrawalItem } from "@/lib/api/flow-instances/withdrawals";
import * as withdrawalsApi from "@/lib/api/flow-instances/withdrawals";

import { goalSnapshotQueryKeys } from "./use-goal-snapshots";

const withdrawalKeys = {
  list: (instanceId: string | null) => ["flowInstances", instanceId, "withdrawals"] as const,
};

/**
 * Withdrawal changes regenerate the instance-level expected value snapshot server-side
 * (see withdrawal-service.ts `regenerateExpectedValue`). Invalidate the cached snapshots
 * so the wizard's forecast and submit-button gating reflect the new withdrawal.
 */
function invalidateInstanceSnapshots(
  queryClient: ReturnType<typeof useQueryClient>,
  instanceId: string,
) {
  queryClient.invalidateQueries({ queryKey: goalSnapshotQueryKeys.instanceCost(instanceId) });
  queryClient.invalidateQueries({
    queryKey: goalSnapshotQueryKeys.instanceExpectedValue(instanceId),
  });
}

export function useWithdrawals(instanceId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: withdrawalKeys.list(instanceId),
    queryFn: () => withdrawalsApi.listWithdrawals(instanceId!),
    enabled: !!instanceId,
  });
  return { withdrawals: data ?? [], error, isLoading };
}

export function useCreateWithdrawal(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: WithdrawalFormData) => withdrawalsApi.createWithdrawal(instanceId, data),
    onSuccess: (result) => {
      if (!result.success || !result.data) return;
      const created = result.data;
      queryClient.setQueryData<WithdrawalItem[]>(withdrawalKeys.list(instanceId), (old) =>
        old ? [...old, created] : [created],
      );
      invalidateInstanceSnapshots(queryClient, instanceId);
    },
  });
}

export function useUpdateWithdrawal(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ withdrawalId, data }: { withdrawalId: string; data: WithdrawalFormData }) =>
      withdrawalsApi.updateWithdrawal(withdrawalId, data),
    onSuccess: (result, { withdrawalId }) => {
      if (!result.success || !result.data) return;
      const updated = result.data;
      queryClient.setQueryData<WithdrawalItem[]>(withdrawalKeys.list(instanceId), (old) =>
        old ? old.map((w) => (w.id === withdrawalId ? updated : w)) : [updated],
      );
      invalidateInstanceSnapshots(queryClient, instanceId);
    },
  });
}

export function useDeleteWithdrawal(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (withdrawalId: string) => withdrawalsApi.deleteWithdrawal(withdrawalId),
    onSuccess: (result, withdrawalId) => {
      if (!result.success) return;
      queryClient.setQueryData<WithdrawalItem[]>(withdrawalKeys.list(instanceId), (old) =>
        old ? old.filter((w) => w.id !== withdrawalId) : [],
      );
      invalidateInstanceSnapshots(queryClient, instanceId);
    },
  });
}
